import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  useToast,
  RadioGroup,
  Radio,
  Stack,
  Box,
  Badge,
} from '@chakra-ui/react';
import { menuService } from '../../../../services/menuService';

const AddDiscountProductModal = ({ isOpen, onClose, onSave, product, products = [] }) => {
  const [selectedProductId, setSelectedProductId] = useState('');
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [discountPrice, setDiscountPrice] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  // Eğer product prop'u varsa (tek ürün için), onu seçili yap
  useEffect(() => {
    if (product && isOpen) {
      setSelectedProductId(product._id);
      
      // Mevcut indirim bilgilerini doldur
      if (product.discountPrice) {
        setDiscountPrice(product.discountPrice.toString());
      }
      if (product.discountType) {
        setDiscountType(product.discountType);
      }
      if (product.discountValue) {
        setDiscountValue(product.discountValue.toString());
      }
      if (product.discountStartDate) {
        const start = new Date(product.discountStartDate);
        // datetime-local format: YYYY-MM-DDTHH:mm
        const year = start.getFullYear();
        const month = String(start.getMonth() + 1).padStart(2, '0');
        const day = String(start.getDate()).padStart(2, '0');
        const hours = String(start.getHours()).padStart(2, '0');
        const minutes = String(start.getMinutes()).padStart(2, '0');
        setStartDate(`${year}-${month}-${day}T${hours}:${minutes}`);
      }
      if (product.discountEndDate) {
        const end = new Date(product.discountEndDate);
        // datetime-local format: YYYY-MM-DDTHH:mm
        const year = end.getFullYear();
        const month = String(end.getMonth() + 1).padStart(2, '0');
        const day = String(end.getDate()).padStart(2, '0');
        const hours = String(end.getHours()).padStart(2, '0');
        const minutes = String(end.getMinutes()).padStart(2, '0');
        setEndDate(`${year}-${month}-${day}T${hours}:${minutes}`);
      }
    } else if (!product && isOpen) {
      // Yeni indirim için formu sıfırla
      setSelectedProductId('');
      setDiscountType('percentage');
      setDiscountValue('');
      setDiscountPrice('');
      setStartDate('');
      setEndDate('');
    }
  }, [product, isOpen]);

  const selectedProduct = products.find(p => p._id === selectedProductId);

  // İndirim fiyatını otomatik hesapla
  useEffect(() => {
    if (selectedProduct && discountType && discountValue) {
      const value = Number(discountValue);
      if (!isNaN(value) && value > 0) {
        if (discountType === 'percentage') {
          const calculatedPrice = selectedProduct.price * (1 - value / 100);
          setDiscountPrice(calculatedPrice.toFixed(2));
        } else {
          // Fixed (TL)
          const calculatedPrice = selectedProduct.price - value;
          setDiscountPrice(Math.max(0, calculatedPrice).toFixed(2));
        }
      }
    }
  }, [selectedProduct, discountType, discountValue]);

  const handleSubmit = async () => {
    if (!selectedProductId) {
      toast({
        title: 'Hata',
        description: 'Lütfen bir ürün seçin',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!discountValue || Number(discountValue) <= 0) {
      toast({
        title: 'Hata',
        description: 'Lütfen geçerli bir indirim değeri girin',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!startDate || !endDate) {
      toast({
        title: 'Hata',
        description: 'Lütfen başlangıç ve bitiş tarihlerini seçin',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (new Date(startDate) >= new Date(endDate)) {
      toast({
        title: 'Hata',
        description: 'Bitiş tarihi başlangıç tarihinden sonra olmalıdır',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!discountPrice || Number(discountPrice) <= 0) {
      toast({
        title: 'Hata',
        description: 'İndirimli fiyat 0\'dan büyük olmalıdır',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      await onSave({
        productId: selectedProductId, // Product ID'yi de gönder
        discountPrice: Number(discountPrice),
        discountType,
        discountValue: Number(discountValue),
        discountStartDate: new Date(startDate).toISOString(),
        discountEndDate: new Date(endDate).toISOString(),
      });
    } catch (error) {
      console.error('Discount save error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveDiscount = async () => {
    if (!selectedProductId) return;
    
    setIsLoading(true);
    try {
      await onSave({
        productId: selectedProductId, // Product ID'yi de gönder
        discountPrice: '',
        discountType: '',
        discountValue: '',
        discountStartDate: '',
        discountEndDate: '',
      });
    } catch (error) {
      console.error('Remove discount error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader color="#e45858">
          {product ? `${product.name} - İndirim Yönet` : 'Ürün Bazlı İndirim'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Ürün Seçimi (sadece product prop'u yoksa) */}
            {!product && (
              <FormControl isRequired>
                <FormLabel fontWeight="bold">Ürün Seçin</FormLabel>
                <Select
                  placeholder="Ürün seçin"
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                >
                  {products.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.name} - {p.price} ₺
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedProduct && (
              <>
                <Box p={3} bg="gray.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.600">Seçili Ürün:</Text>
                  <Text fontWeight="bold">{selectedProduct.name}</Text>
                  <Text fontSize="sm" color="gray.600">Normal Fiyat: {selectedProduct.price} ₺</Text>
                </Box>

                {/* İndirim Tipi */}
                <FormControl isRequired>
                  <FormLabel fontWeight="bold">İndirim Tipi</FormLabel>
                  <RadioGroup value={discountType} onChange={setDiscountType}>
                    <Stack direction="row" spacing={4}>
                      <Radio value="percentage">Yüzde (%)</Radio>
                      <Radio value="fixed">Sabit TL</Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                {/* İndirim Değeri */}
                <FormControl isRequired>
                  <FormLabel fontWeight="bold">
                    {discountType === 'percentage' ? 'İndirim Yüzdesi (%)' : 'İndirim Tutarı (₺)'}
                  </FormLabel>
                  <Input
                    type="number"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    placeholder={discountType === 'percentage' ? 'Örn: 20' : 'Örn: 30'}
                    min={0}
                    max={discountType === 'percentage' ? 100 : undefined}
                  />
                  {discountType === 'percentage' && (
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      Maksimum %100
                    </Text>
                  )}
                </FormControl>

                {/* Hesaplanan İndirimli Fiyat */}
                {discountPrice && (
                  <Box p={3} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                    <HStack justify="space-between">
                      <Text fontWeight="bold">İndirimli Fiyat:</Text>
                      <Badge colorScheme="green" fontSize="md" p={2}>
                        {discountPrice} ₺
                      </Badge>
                    </HStack>
                    {selectedProduct.price && (
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        Normal fiyat: {selectedProduct.price} ₺ → İndirimli: {discountPrice} ₺
                      </Text>
                    )}
                  </Box>
                )}

                {/* Manuel İndirimli Fiyat (opsiyonel) */}
                <FormControl>
                  <FormLabel fontWeight="bold">İndirimli Fiyat (Manuel) (₺)</FormLabel>
                  <Input
                    type="number"
                    value={discountPrice}
                    onChange={(e) => setDiscountPrice(e.target.value)}
                    placeholder="Otomatik hesaplanır veya manuel girebilirsiniz"
                    min={0}
                  />
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Boş bırakılırsa otomatik hesaplanır
                  </Text>
                </FormControl>

                {/* Tarih Aralığı */}
                <HStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel fontWeight="bold">Başlangıç Tarihi</FormLabel>
                    <Input
                      type="datetime-local"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontWeight="bold">Bitiş Tarihi</FormLabel>
                    <Input
                      type="datetime-local"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate || new Date().toISOString().slice(0, 16)}
                    />
                  </FormControl>
                </HStack>

                {startDate && endDate && new Date(endDate) < new Date() && (
                  <Badge colorScheme="red" p={2}>
                    ⚠️ Bitiş tarihi geçmişte! İndirim otomatik olarak pasif olacak.
                  </Badge>
                )}
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            İptal
          </Button>
          {selectedProduct && (product?.discountPrice || product?.isOnSale) && (
            <Button
              colorScheme="red"
              variant="outline"
              mr={3}
              onClick={handleRemoveDiscount}
              isLoading={isLoading}
            >
              İndirimi Kaldır
            </Button>
          )}
          <Button
            colorScheme="orange"
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={!selectedProductId}
          >
            {product ? 'Güncelle' : 'Kaydet'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddDiscountProductModal;
